import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input } from 'reactstrap';
import FocusTrap from 'focus-trap-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Preloader from "../preloader";
import axios from "../../utils/axiosConfig";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { useToast } from "../../contexts/ToastContext";
import "../../constants/service_constants";

function AddUserModal({ isOpen, setIsOpen, siteId, setUpdatePage }) {
    const [email, setEmail] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [selectedRole, setSelectedRole] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const firstInputRef = useRef(null);
    const { getAccessTokenSilently } = useAuth0();
    const { showToast } = useToast();

    useEffect(() => {
        setIsLoading(false);
        setEmail('');
        setIsEmailValid(true);
        setSelectedRole('');
    }, [isOpen]);

    const toggle = () => {
        !isLoading ? setIsOpen(false) : null;
    }

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setIsEmailValid(validateEmail(e.target.value));
    };

    const validateEmail = (email) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };

    const handleRoleSelect = (role) => {
        setSelectedRole(role);
    };

    const handleSubmitClick = () => {
        setIsLoading(true);
        addUserRequest(siteId, email, selectedRole);
    }

    async function addUserRequest(siteId, emailAddress, role) {
        try {
            const token = await getAccessTokenSilently();
            await axios.post(API_ADDRESS + SITE_ADD_USER_ENDPOINT + siteId, {
                user_email: emailAddress,
                role: role
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showToast('User invited successfully!', 'success');
            setIsLoading(false);
            setIsOpen(false);
            setEmail('');
            setIsEmailValid(true);
            setSelectedRole('');
            setUpdatePage(Date.now());
        } catch (error) {
            Sentry.captureException(error);
            showToast('There was an error inviting the user. Please reload the page and try again.', 'error');
            setIsLoading(false);
            setIsOpen(false);
            setEmail('');
            setIsEmailValid(true);
            setSelectedRole('');
        }
    }

    return (
        <div>
            <Modal isOpen={isOpen} toggle={toggle}>
                <ModalHeader toggle={toggle}>Invite a user</ModalHeader>
                { isLoading &&
                    <ModalBody>
                        Submitting your request...
                        <Preloader
                            text={false}
                            inline={true}
                            overlay={false}
                            logo={false}
                        />
                    </ModalBody>
                } { !isLoading &&
                    <ModalBody>
                        <FocusTrap
                            active={isOpen}
                            focusTrapOptions={{
                                initialFocus: () => firstInputRef.current,
                                allowOutsideClick: true,
                                returnFocusOnDeactivate: true,
                            }}
                        >
                        <div>
                            <FormGroup>
                                Please enter the email address of the user you would like to invite to this site.
                                <br/>
                                <em>
                                    (If the user does not already have an account they will be sent an invite to create one.)
                                </em>
                                <br/>
                                <Label for="email">Email:</Label>
                                <Input
                                    ref={firstInputRef}
                                    type="email"
                                    name="email"
                                    id="email"
                                    placeholder="example@example.com"
                                    value={email}
                                    onChange={handleEmailChange}
                                    invalid={!isEmailValid}
                                    aria-invalid={!isEmailValid}
                                />
                                {!isEmailValid && <div className="text-danger">Please enter a valid email address.</div>}
                            </FormGroup>
                            <FormGroup>
                                <Label for="role">Role:</Label>
                                <select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                                    <option value="" disabled>Select a role</option>
                                    <option value="admin">Admin</option>
                                    <option value="view">View</option>
                                </select>
                            </FormGroup>
                        </div>
                        </FocusTrap>
                    </ModalBody>
                }
                <ModalFooter>
                    {isEmailValid && selectedRole && !isLoading &&
                    <Button color="primary" type="submit" onClick={handleSubmitClick} className="ml-auto">
                        Submit
                    </Button>
                    } {!isEmailValid || !selectedRole &&
                    <p>Please enter a valid email and select a role.</p>
                    }
                </ModalFooter>
            </Modal>
        </div>
    );
}

export default AddUserModal;